import { surfaceLift } from '../../shared/geo.js';
import * as THREE from 'three';
import { resolveCircle } from '../../shared/collision.js';
import { pointInPolygon } from '../../shared/clip.js';
import { makeCharacter, animateCharacter } from './character.js';

const WALK = 2.4;
const RUN = 6.5;
const RADIUS = 0.35;
const GRAVITY = 18;

// The player's own character on foot.
export class Player {
  constructor(scene, map, chunks, look) {
    this.map = map;
    this.chunks = chunks;
    this.look = look || {};
    this.char = makeCharacter(look);
    scene.add(this.char.group);
    this.x = 0;
    this.z = 0;
    this.y = 0;
    this.vy = 0;
    this.yaw = 0; // direction the character faces; 0 looks toward +z
    this.speed = 0;
    this.grounded = true;
    this.colliders = [];
    this.builder = null; // set by the game: parts players built block and carry the player too
    this.bounds = map.meta.bounds;
    this.characters = null; // set by the game: the pool of real bodies
    this.body = null; // the player's own real body, once one is lent
    this.tone = look && look.skin ? 1 - Math.min(1, Math.max(0, parseInt(String(look.skin).replace('#', '').slice(0, 2), 16) / 255)) : 0.6;
  }

  // Keeps the real body on the box figure's spot: borrowed when the pool is ready, posed by the
  // clip the moment asks for (walking by speed, or `key` such as 'sit' on a bike).
  poseBody(dt, key = null) {
    if (!this.characters) return;
    if (!this.body && this.characters.ready) {
      this.body = this.characters.borrow(1, this.tone, this.look.shirt || '#f2b705', this.look.trousers || '#2a2a30');
      if (!this.body) return;
      this.char.body.visible = false;
    }
    if (!this.body) return;
    const g = this.char.group;
    this.body.root.visible = g.visible;
    if (!g.visible) return;
    this.body.root.position.copy(g.position);
    this.body.root.rotation.copy(g.rotation);
    if (key) this.characters.play(this.body, key, 0.2);
    else this.characters.pose(this.body, g.position.x, g.position.y, g.position.z, g.rotation.y, this.speed, 0);
    // where the feet are in the stride, for the footsteps: 0..1 through the walking clip
    const action = this.body.current;
    this.footPhase = !key && action && this.speed > 0.15 ? (action.time / action.getClip().duration) % 1 : null;
    this.body.mixer.update(dt);
  }

  place(x, z) {
    this.x = x;
    this.z = z;
    this.y = this.map.heightmap.heightAt(x, z);
    this.vy = 0;
    this.sync();
  }

  // move: { x, z } intent relative to the camera. camYaw: the camera's heading.
  update(dt, move, running, jump, camYaw) {
    const len = Math.hypot(move.x, move.z);
    let target = 0;
    if (len > 0) {
      // camera looks along (sin yaw, cos yaw); right of that is (-cos yaw, sin yaw)
      const fx = Math.sin(camYaw);
      const fz = Math.cos(camYaw);
      const dx = (fx * move.z - fz * move.x) / len;
      const dz = (fz * move.z + fx * move.x) / len;
      target = (running ? RUN : WALK) * (this.pace || 1);
      const want = Math.atan2(dx, dz);
      let diff = want - this.yaw;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      this.yaw += diff * Math.min(1, dt * 12);
      // steep ground slows the climb
      const ahead = this.map.heightmap.heightAt(this.x + dx, this.z + dz) - this.y;
      const slope = Math.max(0, ahead);
      const pace = this.speed / (1 + slope * 1.4);
      this.x += dx * pace * dt;
      this.z += dz * pace * dt;
    }
    this.speed += (target - this.speed) * Math.min(1, dt * 9);

    const b = this.bounds;
    this.x = Math.max(b.x0 + 5, Math.min(b.x1 - 5, this.x));
    this.z = Math.max(b.z0 + 5, Math.min(b.z1 - 5, this.z));
    const blocking = this.chunks.collidersNear(this.x, this.z, this.colliders);
    if (this.obstacles) this.obstacles(this.x, this.z, blocking);
    if (this.builder) this.builder.collect(this.x, this.z, this.y, blocking);
    const fixed = resolveCircle(this.x, this.z, RADIUS, blocking);
    this.x = fixed.x;
    this.z = fixed.z;

    const lifted = this.map.heightmap.heightAt(this.x, this.z) + surfaceLift(this.chunks.surfaceAt(this.x, this.z));
    const ground = this.builder ? Math.max(this.builder.groundAt(this.x, this.z, this.y), lifted) : lifted;
    if (this.grounded) {
      this.y = ground;
      if (jump) {
        this.vy = 5.2;
        this.grounded = false;
      }
    } else {
      this.vy -= GRAVITY * dt;
      this.y += this.vy * dt;
      if (this.y <= ground) {
        this.y = ground;
        this.vy = 0;
        this.grounded = true;
      }
    }
    animateCharacter(this.char, this.speed, dt);
    this.sync();
    this.poseBody(dt);
  }

  sync() {
    this.char.group.position.set(this.x, this.y, this.z);
    this.char.group.rotation.y = this.yaw;
  }
}

// Third-person camera that orbits a target and stays above the ground.
export class OrbitCamera {
  constructor(camera, heightmap) {
    this.camera = camera;
    this.heightmap = heightmap;
    this.yaw = 0; // heading the camera looks along
    this.pitch = 0.32; // radians above the horizontal
    this.distance = 6.5;
    this.reach = 6.5; // how far out the camera actually sits after wall checks
    this.viewPitch = 0.32; // the pitch actually used, which may be steeper near walls
    this.target = new THREE.Vector3();
    this.goal = new THREE.Vector3();
    this.hits = [];
  }

  look(dx, dy, wheel) {
    this.yaw -= dx * 0.0032;
    this.pitch = Math.max(-0.25, Math.min(1.45, this.pitch + dy * 0.0026));
    if (wheel) this.distance = Math.max(2.2, Math.min(90, this.distance * Math.exp(wheel * 0.0012)));
  }

  // How far the camera can sit from the target at this pitch before a building is in the way.
  freeReach(pitch, chunks) {
    if (!chunks) return this.distance;
    const c = Math.cos(pitch);
    const dirX = -Math.sin(this.yaw) * c;
    const dirY = Math.sin(pitch);
    const dirZ = -Math.cos(this.yaw) * c;
    const steps = 14;
    for (let s = 1; s <= steps; s++) {
      const d = (this.distance * s) / steps;
      const sx = this.target.x + dirX * d;
      const sz = this.target.z + dirZ * d;
      const sy = this.target.y + dirY * d;
      for (const b of chunks.collidersNear(sx, sz, this.hits)) {
        if (sy < b.top + 0.4 && pointInPolygon(sx, sz, b.p)) return Math.max(0.3, (this.distance * (s - 1)) / steps - 0.25);
      }
    }
    return this.distance;
  }

  // Places the camera behind the target point (x, y, z). chunks supplies the
  // nearby buildings so the camera stops at a wall instead of passing through.
  update(x, y, z, dt, chunks) {
    this.target.lerp(this.goal.set(x, y, z), Math.min(1, dt * 14));
    // With a wall close behind, lift the view over it rather than sit inside it:
    // try the wanted angle first, then steeper ones, and keep the roomiest.
    let pitch = this.pitch;
    let reach = this.freeReach(pitch, chunks);
    if (reach < Math.min(3, this.distance)) {
      for (const steeper of [0.75, 1.1, 1.4]) {
        if (steeper <= this.pitch) continue;
        const r = this.freeReach(steeper, chunks);
        if (r > reach + 0.5) {
          reach = r;
          pitch = steeper;
        }
        if (reach >= Math.min(3, this.distance)) break;
      }
    }
    // tighten at once, but ease back out so the view does not jump as walls pass
    this.reach = reach < this.reach ? reach : this.reach + (reach - this.reach) * Math.min(1, dt * 6);
    this.viewPitch += (pitch - this.viewPitch) * Math.min(1, dt * 10);
    const c = Math.cos(this.viewPitch);
    const cx = this.target.x - Math.sin(this.yaw) * c * this.reach;
    const cz = this.target.z - Math.cos(this.yaw) * c * this.reach;
    let cy = this.target.y + Math.sin(this.viewPitch) * this.reach;
    const floor = this.heightmap.heightAt(cx, cz) + 0.4;
    if (cy < floor) cy = floor;
    this.camera.position.set(cx, cy, cz);
    this.camera.lookAt(this.target);
  }
}
